import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import { useEffect, useRef } from "react";

import AppLogoIcon from "@/components/app-logo-icon";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useLeadDomain } from "@/hooks/use-lead-domain";
import { stripProtocol } from "@/lib/utils";
import { signInPath, signUpPath } from "@/routes";
// Helper to track events with Rybbit + Ahoy (client-side only)
const trackEvent = async (name: string, props?: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  window.rybbit?.event(name, props as Record<string, string> | undefined);
  try {
    const { default: ahoy } = await import("ahoy.js");
    ahoy.track(name, props);
  } catch {
    // no-op
  }
};

const PENDING_GENERATION_URL_KEY = "pending_generation_url";
const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const SIGN_IN_HREF = `${signInPath()}?return_to=${encodeURIComponent("/new")}`;

// Basic cleanup without adding protocol - for real-time input handling
const cleanInputUrl = (rawValue: string) => {
  let input = rawValue.trim();

  // Remove leading/trailing whitespace and invisible chars
  input = input.replace(/^[\s\u200B-\u200D\uFEFF]+|[\s\u200B-\u200D\uFEFF]+$/g, "");

  // Remove accidental spaces in the middle
  input = input.replace(/\s+/g, "");

  return input;
};

// Full sanitization with protocol addition - for form submission only
const sanitizeUrl = (rawValue: string) => {
  const input = cleanInputUrl(rawValue);

  // If URL doesn't have protocol, add https://
  if (input && !/^https?:\/\//i.test(input) && /^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(input)) {
    return `https://${input}`;
  }

  return input;
};

const hostnameFromPageUrl = (rawValue: string) => {
  const cleaned = sanitizeUrl(rawValue);
  if (!cleaned || !/^https?:\/\//i.test(cleaned)) return null;

  try {
    const hostname = new URL(cleaned).hostname.replace(/^www\./i, "").toLowerCase();
    return DOMAIN_PATTERN.test(hostname) ? hostname : null;
  } catch {
    return null;
  }
};

const rememberPendingUrl = (cleanedUrl: string) => {
  try {
    if (cleanedUrl && /^https?:\/\//i.test(cleanedUrl)) {
      window.sessionStorage.setItem(PENDING_GENERATION_URL_KEY, cleanedUrl);
      return;
    }

    window.sessionStorage.removeItem(PENDING_GENERATION_URL_KEY);
  } catch {
    // sessionStorage can be blocked
  }
};

const readPendingUrl = () => {
  try {
    const pending = window.sessionStorage.getItem(PENDING_GENERATION_URL_KEY);
    if (pending && /^https?:\/\//i.test(pending)) return pending;
  } catch {
    // sessionStorage can be blocked
  }

  return null;
};

const GUEST_STEPS: {
  step: string;
  title: string;
  description: string;
  image: string;
}[] = [
  {
    step: "STEP 1",
    title: "Paste a URL",
    description: "Enter any landing page, event, product, podcast episode, blog post, or book page URL.",
    image: "/how-it-works/step-1.jpg",
  },
  {
    step: "STEP 2",
    title: "Create a free account",
    description: "Start a 7-day trial. No card. We keep the domain from your URL for onboarding.",
    image: "/how-it-works/step-2.jpg",
  },
  {
    step: "STEP 3",
    title: "Download and use",
    description: "Grab your OG image and add it to your site or share it anywhere.",
    image: "/how-it-works/step-3.jpg",
  },
];

const SIGNED_IN_STEPS: {
  step: string;
  title: string;
  description: string;
  image: string;
}[] = [
  {
    step: "STEP 1",
    title: "Paste a URL",
    description: "Enter any landing page, event, product, podcast episode, blog post, or book page URL.",
    image: "/how-it-works/step-1.jpg",
  },
  {
    step: "STEP 2",
    title: "We generate your image",
    description: "We pull your title, colors, and favicon, then create branded OG image variations.",
    image: "/how-it-works/step-2.jpg",
  },
  {
    step: "STEP 3",
    title: "Download and use",
    description: "Grab your OG image and add it to your site or share it anywhere.",
    image: "/how-it-works/step-3.jpg",
  },
];

const GUEST_BENEFITS = ["7-day free trial", "No credit card", "Then generate the image"];
const SIGNED_IN_BENEFITS = ["2 image variations", "Unlimited refinements", "No design tools required"];

export default function NewPage() {
  const { flags, auth, initial_url: initialUrl } = usePage<{ initial_url?: string | null }>().props;
  const isLoggedIn = Boolean(auth?.user);
  const { setLeadDomain } = useLeadDomain();
  const { data, setData, processing, errors } = useForm<{
    url: string;
    email: string;
    style: string;
  }>({
    url: typeof initialUrl === "string" ? initialUrl : "",
    email: "",
    style: "default",
  });
  const inputUrlRef = useRef<HTMLInputElement>(null);
  const didRestorePendingUrl = useRef(false);

  useEffect(() => {
    if (didRestorePendingUrl.current) return;
    didRestorePendingUrl.current = true;
    if (typeof initialUrl === "string" && initialUrl.length > 0) return;

    const pending = readPendingUrl();
    if (pending) setData("url", pending);
  }, [initialUrl, setData]);

  const updateInputUrl = (rawValue: string) => {
    const cleanedUrl = cleanInputUrl(rawValue);
    setData("url", cleanedUrl);
  };

  const handleInputUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateInputUrl(event.target.value);
  };

  const handleInputUrlPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) return;

    updateInputUrl(stripProtocol(pastedText));
  };

  const prepareAccountHandoff = () => {
    const cleanedUrl = sanitizeUrl(data.url);
    const domain = hostnameFromPageUrl(cleanedUrl);
    if (domain) setLeadDomain(domain);
    rememberPendingUrl(cleanedUrl);
    return { cleanedUrl, domain };
  };

  const trackCreateAccount = (cleanedUrl: string, domain: string | null) => {
    void trackEvent("click-new-create-account", {
      ...(cleanedUrl ? { url: cleanedUrl } : {}),
      ...(domain ? { domain } : {}),
    });
  };

  const trackLogIn = (cleanedUrl: string) => {
    rememberPendingUrl(cleanedUrl);
    void trackEvent("click-new-log-in", cleanedUrl ? { url: cleanedUrl } : {});
  };

  const continueToSignUp = () => {
    const { cleanedUrl, domain } = prepareAccountHandoff();
    trackCreateAccount(cleanedUrl, domain);
    router.visit(signUpPath());
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isLoggedIn) {
      continueToSignUp();
      return;
    }

    const cleanedUrl = sanitizeUrl(data.url);
    if (!cleanedUrl) return;

    const trimmedEmail = data.email.trim();

    if (flags.free_one_off_generation) {
      void trackEvent("click-generate-free", {
        url: cleanedUrl,
        style: data.style,
      });

      const payload: { url: string; email?: string; style: string } = {
        url: cleanedUrl,
        style: data.style,
      };
      if (trimmedEmail) {
        payload.email = trimmedEmail;
      }

      router.post(`/make-for-free`, payload, {
        onError: (error) => {
          console.error(error);
        },
      });
    } else {
      void trackEvent("click-checkout", { url: cleanedUrl });

      // Use full page redirect instead of AJAX to allow external redirects
      router.get(`/checkout?url=${encodeURIComponent(cleanedUrl)}`, {}, { replace: false });
    }
  };

  const signedInLabel = flags.free_one_off_generation ? "Generate for free" : "Get started for $2";
  const steps = isLoggedIn ? SIGNED_IN_STEPS : GUEST_STEPS;
  const benefits = isLoggedIn ? SIGNED_IN_BENEFITS : GUEST_BENEFITS;

  return (
    <>
      <Head title="Get a branded OG image in seconds" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 px-4 py-12 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="mx-auto max-w-5xl">
          {!isLoggedIn ? (
            <div className="mb-8 flex items-center justify-end gap-4 text-sm">
              <Link
                href={SIGN_IN_HREF}
                onClick={() => trackLogIn(sanitizeUrl(data.url))}
                className="font-medium text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white"
              >
                Log in
              </Link>
              <Link
                href={signUpPath()}
                onClick={() => {
                  const { cleanedUrl, domain } = prepareAccountHandoff();
                  trackCreateAccount(cleanedUrl, domain);
                }}
                className="rounded-full bg-white px-4 py-2 font-semibold text-orange-600 shadow-sm ring-1 ring-orange-200 hover:bg-orange-50 dark:bg-gray-900 dark:text-orange-300 dark:ring-orange-500/40"
              >
                Create a free account
              </Link>
            </div>
          ) : null}

          {/* Logo centered */}
          <div className="mb-12 flex justify-center">
            <Link href="/" className="flex items-center gap-3">
              <div className="flex aspect-square size-10 items-center justify-center rounded-xl bg-orange-500 shadow-lg">
                <AppLogoIcon className="size-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">OG Pilot</span>
            </Link>
          </div>

          {/* Headline + Subheadline */}
          <h1 className="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
            Get a branded OG image in seconds
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-center text-lg text-gray-600 dark:text-gray-300">
            {isLoggedIn
              ? "Paste your URL. We generate a scroll-stopping social preview."
              : "Paste a page URL, then create a free account. The 7-day trial does not need a card."}
          </p>

          {/* Form - single row on large screens */}
          <form className="flex flex-col gap-3 md:flex-row md:items-center" onSubmit={handleSubmit}>
            <div className="flex flex-1 items-center rounded-full bg-white px-4 py-2 shadow-sm ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-orange-500 dark:bg-gray-800 dark:ring-gray-700 dark:focus-within:ring-orange-400">
              <label htmlFor="page-url" className="sr-only">
                Page URL
              </label>
              <Input
                ref={inputUrlRef}
                id="page-url"
                name="url"
                type="text"
                placeholder="yourpage.com"
                className="flex-1 border-0 bg-transparent text-base shadow-none placeholder:text-gray-400 focus-visible:ring-0 dark:text-white dark:placeholder:text-gray-500"
                value={data.url}
                onChange={handleInputUrlChange}
                onPaste={handleInputUrlPaste}
                autoFocus
                required={isLoggedIn}
              />
            </div>
            <Button
              type="submit"
              disabled={processing}
              className="h-12 rounded-full bg-orange-500 px-6 text-base font-semibold text-white shadow transition-colors duration-200 hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:h-auto md:py-3"
            >
              {processing ? "Processing..." : isLoggedIn ? signedInLabel : "Create a free account"}
            </Button>
          </form>

          {!isLoggedIn ? (
            <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-300">
              Already have an account?{" "}
              <Link
                href={SIGN_IN_HREF}
                onClick={() => trackLogIn(sanitizeUrl(data.url))}
                className="font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400"
              >
                Log in
              </Link>
            </p>
          ) : null}

          {errors.url ? <p className="mt-4 text-center text-sm text-red-600 dark:text-red-300">{errors.url}</p> : null}

          {/* Form footer with benefit labels */}
          <div className="mt-8 mb-16 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-500" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>

          {/* Steps Section - 3 columns on large, 1 column on small */}
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((step) => (
              <Card
                key={step.title}
                className="border border-orange-200/40 bg-white/95 shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/80"
              >
                <CardHeader className="pb-4">
                  <div className="mx-auto mb-4 overflow-hidden rounded-lg bg-orange-50 dark:bg-orange-900/20">
                    <img src={step.image} alt={step.title} className="h-40" />
                  </div>
                  <p className="text-xs font-medium tracking-wide text-orange-600 uppercase dark:text-orange-400">
                    {step.step}
                  </p>
                  <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">{step.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
